import { FC, useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Download } from "lucide-react";
import ExportOptionsForm, {
	ExportOptionsFormState
} from "src/features/observationsAdmin/components/ExportOptionsForm/ExportOptionsForm";
import { DEFAULT_EXPORT_OPTIONS } from "src/features/observationsAdmin/constants";
import { calculateExportDateRange } from "src/features/observationsAdmin/utils";
import {
	useObservationFormYears,
	useAllObservationsReport,
	useAllStoresReport,
	useAllUsersReport,
	useByUserObservationsReport
} from "src/features/reporting/hooks";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import { SelectedExport } from "src/pages/ReportDetail/ReportDetail";

export interface ExportOptionsModalProps {
	formId: number;

	userId?: string;

	selectedExport: SelectedExport;

	isOpen: boolean;

	showDateFilters: boolean;

	onCancel: () => void;
}

const ExportOptionsModal: FC<ExportOptionsModalProps> = ({
	formId,
	userId,
	selectedExport,
	isOpen,
	onCancel,
	showDateFilters
}) => {
	const { data: obsevationYears } = useObservationFormYears(formId);

	const {
		mutate: downloadAllObservationsReport,
		isLoading: isSendingAllObservationsReport
	} = useAllObservationsReport();

	const { mutate: downloadAllStoresReport, isLoading: isSendingStoreReport } =
		useAllStoresReport();

	const { mutate: downloadAllUsersReport, isLoading: isSendingUsersReport } =
		useAllUsersReport();

	const {
		mutate: downloadUseByUserObservationsReport,
		isLoading: isSendingByUsersReport
	} = useByUserObservationsReport();

	const methods = useForm<ExportOptionsFormState>({
		defaultValues: DEFAULT_EXPORT_OPTIONS
	});

	const { control, reset } = methods;

	const { exportType, month, quarter, year } = useWatch({ control });

	useEffect(() => {
		if (!isOpen) return;

		reset(DEFAULT_EXPORT_OPTIONS);
	}, [isOpen, reset]);

	const handleSubmit = () => {
		if (showDateFilters) {
			const { startDate, endDate } = calculateExportDateRange({
				exportType,
				month,
				quarter,
				year
			});

			if (!startDate || !endDate) return;

			if (selectedExport === "ALL_OBSERVATIONS") {
				downloadAllObservationsReport(
					{ formId, startDate, endDate },
					{ onSettled: onCancel }
				);
			}

			if (selectedExport === "ALL_STORES") {
				downloadAllStoresReport(
					{ formId, startDate, endDate },
					{ onSettled: onCancel }
				);
			}

			if (selectedExport === "ALL_USERS") {
				downloadAllUsersReport(
					{ formId, startDate, endDate },
					{ onSettled: onCancel }
				);
			}
		}

		if (selectedExport === "BY_USER") {
			downloadUseByUserObservationsReport(
				{ formId, userId },
				{ onSettled: onCancel }
			);
		}
	};

	const isLoading =
		isSendingStoreReport ||
		isSendingUsersReport ||
		isSendingAllObservationsReport ||
		isSendingByUsersReport;

	const years = obsevationYears?.results.map(({ year }) => year);

	return (
		<ConfirmationModal
			isOpen={isOpen}
			title="Export report"
			description="Your report will be compiled and sent to your email address. Check your spam folder if you don't see it shortly."
			icon={<Download className="h-10 w-10" data-testid="help-icon" />}
			onCancel={onCancel}
			onSubmit={handleSubmit}
			submitBtnProps={{
				children: "Send XLSX",
				isLoading
			}}
		>
			{showDateFilters && (
				<div
					className="mt-6 w-full px-5 py-4 rounded-xl border border-border bg-subtle"
					data-testid="export-filters"
				>
					<FormProvider {...methods}>
						<ExportOptionsForm years={years} />
					</FormProvider>
				</div>
			)}
		</ConfirmationModal>
	);
};

export default ExportOptionsModal;
